import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  StatusIndicator,
  ColumnLayout,
  Table,
  Alert,
  TextContent,
  BarChart,
  Modal,
  Button,
  LiveRegion,
  Badge,
  ExpandableSection,
} from '@cloudscape-design/components';
import LoadingBar from "@cloudscape-design/chat-components/loading-bar";
import SupportPromptGroup from "@cloudscape-design/chat-components/support-prompt-group";
import { useDemoContext } from '../context/DemoContext';
import apiService from '../services/api';
import PromptResponseModal from '../components/PromptResponseModal';
import CitationPanel from '../components/CitationPanel';

function AnalysisPage() {
  const navigate = useNavigate();
  const { 
    selectedFile, 
    manualAnswers, 
    manualAnalysisTime, 
    analysisCompleted,
    resetDemo,
    completeAIAnalysis
  } = useDemoContext();
  
  const [analysisStatus, setAnalysisStatus] = useState(
    analysisCompleted ? 'complete' : 'not-started'
  );
  const [showStartModal, setShowStartModal] = useState(!analysisCompleted);
  const [aiAnalysisTime, setAiAnalysisTime] = useState(3.2);
  const [aiAccuracy, setAiAccuracy] = useState(100);
  const [questionsData, setQuestionsData] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPromptResponseModal, setShowPromptResponseModal] = useState(false);
  const [expandedItems, setExpandedItems] = useState([]);

  // Check if manual analysis was skipped
  const manualAnalysisSkipped = !manualAnalysisTime || manualAnalysisTime === 0;

  // Redirect if no file is selected
  useEffect(() => {
    if (!selectedFile && !analysisCompleted) {
      navigate('/upload');
    }
  }, [selectedFile, navigate, analysisCompleted]);

  // Load questions for the selected file
  useEffect(() => {
    if (selectedFile && !analysisCompleted) {
      const loadQuestions = async () => {
        try {
          console.log('Loading questions for file:', selectedFile.id);
          const questions = await apiService.getQuestions(selectedFile.id);
          console.log('Loaded questions:', questions);
          setQuestionsData(questions);
        } catch (err) {
          console.error('Error loading questions:', err);
        }
      };
      
      loadQuestions();
    }
  }, [selectedFile, analysisCompleted]);

  // Load AI analysis results if analysis is already completed
  useEffect(() => {
    if (analysisCompleted && selectedFile) {
      const loadAnalysisResults = async () => {
        try {
          const fileId = selectedFile ? selectedFile.id : 'file1';
          console.log(`Loading analysis results for file: ${fileId}`);
          const results = await apiService.getAIAnalysis(fileId);
          console.log('Analysis results:', results);
          setAiAnalysisTime(results.aiAnalysisTime);
          setAiAccuracy(results.aiAccuracy);
          setQuestionsData(results.questionsData);
        } catch (err) {
          setError('Failed to load analysis results. Please try again.');
          console.error('Error loading analysis results:', err);
        }
      };

      loadAnalysisResults();
    }
  }, [analysisCompleted, selectedFile]);

  const startAnalysis = async () => {
    setShowStartModal(false);
    setAnalysisStatus('in-progress');
    setIsLoading(true);
    
    try {
      console.log('Starting AI analysis...');
      console.log('Selected file:', selectedFile);
      console.log('Manual answers:', manualAnswers);
      
      // Try real AI research first
      try {
        console.log('Attempting real AI research...');
        
        // Get the questions for AI research
        const manualQuestions = await apiService.getQuestions(selectedFile.id);
        console.log('Manual questions for AI:', manualQuestions);
        
        // Run AI research with progress updates
        const results = await apiService.runAIResearch(
          selectedFile.company || 'amazon', // Use file company or default
          manualQuestions,
          true, // demoMode
          (progress) => {
            console.log('AI research progress:', progress);
            // Update UI with progress if needed
            if (progress.progress) {
              // Could update a progress bar here
            }
          }
        );
        
        console.log('AI research completed:', results);
        
        // Transform AI results to match expected format
        const transformedResults = {
          aiAnalysisTime: results.duration || 3.2,
          aiAccuracy: 100, // Could calculate based on confidence scores
          questionsData: results.results || results.questionsData || []
        };
        
        // Update state with real AI results
        setAiAnalysisTime(transformedResults.aiAnalysisTime);
        setAiAccuracy(transformedResults.aiAccuracy);
        setQuestionsData(transformedResults.questionsData);
        
        console.log('Real AI research successful');
        
      } catch (aiError) {
        console.warn('Real AI research failed, falling back to mock:', aiError);
        
        // Fallback to mock logic with simulated delay
        const simulateApiCall = new Promise((resolve) => {
          setTimeout(async () => {
            try {
              // Map document IDs to file IDs for mock API
              const documentToFileMapping = {
                'amazon-annual-report-2024': 'file1',
                'nab-annual-report-2024': 'file2', 
                'iag-pds': 'file3'
              };
              
              const fileId = documentToFileMapping[selectedFile.id] || 'file1';
              console.log(`Falling back to mock analysis for file: ${selectedFile.id} -> ${fileId}`);
              
              const results = await apiService.submitManualAnalysis(
                fileId,
                manualAnswers,
                manualAnalysisTime
              );
              console.log('Mock analysis results:', results);
              resolve(results);
            } catch (error) {
              resolve({ error });
            }
          }, 3200); // 3.2 seconds delay for mock
        });
        
        // Wait for the simulated API call to complete
        const mockResults = await simulateApiCall;
        
        if (mockResults.error) {
          throw mockResults.error;
        }
        
        // Update state with mock results
        setAiAnalysisTime(mockResults.aiAnalysisTime);
        setAiAccuracy(mockResults.aiAccuracy);
        setQuestionsData(mockResults.questionsData);
        
        console.log('Mock analysis fallback successful');
      }
      
      // Mark analysis as complete
      setAnalysisStatus('complete');
      completeAIAnalysis();
      
    } catch (err) {
      setError('Failed to complete analysis. Please try again.');
      console.error('Error during analysis:', err);
      setAnalysisStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    resetDemo();
    navigate('/');
  };

  const handleGoToManualAnalysis = () => {
    navigate('/manual-analysis');
  };

  const renderStatusIndicator = () => {
    switch (analysisStatus) {
      case 'not-started':
        return <StatusIndicator type="pending">Ready to start</StatusIndicator>;
      case 'in-progress':
        return <StatusIndicator type="in-progress">Analysis in progress</StatusIndicator>;
      case 'complete':
        return <StatusIndicator type="success">Analysis complete</StatusIndicator>;
      case 'error':
        return <StatusIndicator type="error">Analysis failed</StatusIndicator>;
      default:
        return null;
    }
  };

  // Format time in seconds to a readable format
  const formatTime = (seconds) => {
    if (!seconds) return 'Not completed';
    if (seconds < 60) {
      return `${seconds.toFixed(1)} seconds`;
    } else {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = (seconds % 60).toFixed(1);
      return `${minutes} min ${remainingSeconds} sec`;
    }
  };

  // Calculate time difference and percentage
  const timeDifference = manualAnalysisTime - aiAnalysisTime;
  const timePercentageDifference = manualAnalysisTime ? 
    ((manualAnalysisTime - aiAnalysisTime) / aiAnalysisTime * 100).toFixed(1) : 0;

  const [manualAccuracy, setManualAccuracy] = useState(0);
  const [manualAccuracyCalculated, setManualAccuracyCalculated] = useState(false);

  // Calculate accuracy when dependencies change, but only if not already calculated
  useEffect(() => {
    // Don't recalculate if we already have a valid accuracy and questionsData is empty
    if (manualAccuracyCalculated && (!questionsData || questionsData.length === 0)) {
      return;
    }
    
    if (!manualAnswers || Object.keys(manualAnswers).length === 0 || !questionsData || questionsData.length === 0) {
      if (!manualAccuracyCalculated) {
        setManualAccuracy(0);
      }
      return;
    }

    let correctCount = 0;
    let totalQuestions = questionsData.length;

    questionsData.forEach(question => {
      const questionId = question.id;
      const correctAnswer = question.correctAnswer;
      const userAnswer = manualAnswers[questionId];
      
      if (userAnswer === correctAnswer) {
        correctCount++;
      }
    });
    
    const accuracy = (correctCount / totalQuestions) * 100;
    setManualAccuracy(accuracy);
    setManualAccuracyCalculated(true);
  }, [manualAnswers, questionsData, manualAccuracyCalculated]);

  // Determine which time is faster (lower)
  const isAIFaster = aiAnalysisTime < manualAnalysisTime;

  if (error) {
    return (
      <Container>
        <SpaceBetween size="l">
          <Header variant="h1">Error</Header>
          <Alert type="error">{error}</Alert>
          <Button onClick={handleReset}>Reset Demo</Button>
        </SpaceBetween>
      </Container>
    );
  }

  return (
    <Container>
      <SpaceBetween size="l">
        <Header
          variant="h1"
          info={renderStatusIndicator()}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button iconName="refresh" onClick={handleReset}>Reset Demo</Button>
              {analysisStatus === 'not-started' && !manualAnalysisSkipped && (
                <Button variant="primary" iconName="gen-ai" onClick={() => setShowStartModal(true)}>
                  Start AI Analysis
                </Button>
              )}
            </SpaceBetween>
          }
        >
          AI Document Analysis
        </Header>

        {manualAnalysisSkipped && (
          <Alert type="error">
            <SpaceBetween size="xs">
              <div>
                The manual analysis step was skipped. To make a proper comparison between manual and AI analysis, 
                please complete the manual analysis step first.
              </div>
              <Button onClick={handleGoToManualAnalysis}>Go to Manual Analysis</Button>
            </SpaceBetween>
          </Alert>
        )}

        {!manualAnalysisSkipped && manualAnswers && analysisStatus !== 'not-started' && (
          <Alert 
            type="info"
            action={
              <Button 
                iconName="gen-ai" 
                onClick={() => setShowPromptResponseModal(true)}
              >
                View Amazon Bedrock Prompt & Response
              </Button>
            }
          >
            Thank you for completing the manual analysis. Now our AI is analyzing the document to provide comprehensive insights.
          </Alert>
        )}

        {analysisStatus === 'not-started' && !manualAnalysisSkipped && (
          <Box textAlign="center" padding="xl">
            <SpaceBetween size="l">
              <TextContent>
                <h2>Ready to Start AI Analysis</h2>
                <p>
                  You've completed the manual analysis of the document in {formatTime(manualAnalysisTime)}.
                </p>
                <p>
                  Now, let's see how quickly and accurately AI can analyze the same document.
                </p>
              </TextContent>
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="refresh" onClick={handleReset}>Reset Demo</Button>
                <Button variant="primary" iconName="gen-ai" onClick={() => setShowStartModal(true)}>
                  Start AI Analysis
                </Button>
              </SpaceBetween>
            </SpaceBetween>
          </Box>
        )}

        {analysisStatus === 'in-progress' && !manualAnalysisSkipped && (
          <Box>
            <SpaceBetween size="m">
              <LiveRegion>
                <Box margin={{ bottom: "xs" }} color="text-body-secondary">
                  Analyzing document with Amazon Bedrock
                </Box>
                <LoadingBar variant="gen-ai" />
              </LiveRegion>
              
              <ColumnLayout columns={2} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Document</Box>
                  <Box variant="awsui-value-large">
                    {selectedFile ? selectedFile.name : 'AusSuper Product Disclosure Statement'}
                  </Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Status</Box>
                  <Box variant="awsui-value-large">
                    Waiting for Amazon Bedrock response...
                  </Box>
                </div>
              </ColumnLayout>
            </SpaceBetween>
          </Box>
        )}

        {analysisStatus === 'complete' && (
          <SpaceBetween size="l">
            <Header variant="h2">Manual vs AI Analysis Comparison</Header>
            
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Manual Analysis Time</Box>
                <Box variant="awsui-value-large">
                  <Badge color={manualAnalysisSkipped ? 'grey' : (isAIFaster ? 'red' : 'green')}>
                    {manualAnalysisSkipped ? 'Not completed' : formatTime(manualAnalysisTime)}
                  </Badge>
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">AI Analysis Time</Box>
                <Box variant="awsui-value-large">
                  <Badge color={manualAnalysisSkipped ? 'grey' : (isAIFaster ? 'green' : 'red')}>
                    {formatTime(aiAnalysisTime)}
                  </Badge>
                </Box>
              </div>
              
              {!manualAnalysisSkipped && (
                <>
                  <div>
                    <Box variant="awsui-key-label">Time Saved</Box>
                    <Box variant="awsui-value-large">{formatTime(timeDifference)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Efficiency Improvement</Box>
                    <Box variant="awsui-value-large">{timePercentageDifference}%</Box>
                  </div>
                </>
              )}
              
              <div>
                <Box variant="awsui-key-label">Manual Analysis Accuracy</Box>
                <Box variant="awsui-value-large">
                  <Badge color={manualAnalysisSkipped ? 'grey' : (manualAccuracy < aiAccuracy ? 'red' : 'green')}>
                    {manualAnalysisSkipped ? 'Not completed' : `${manualAccuracy.toFixed(0)}%`}
                  </Badge>
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">AI Analysis Accuracy</Box>
                <Box variant="awsui-value-large">
                  <Badge color={manualAnalysisSkipped ? 'grey' : (aiAccuracy > manualAccuracy ? 'green' : 'red')}>
                    {aiAccuracy}%
                  </Badge>
                </Box>
              </div>
            </ColumnLayout>
            
            {manualAnalysisSkipped ? (
              <Alert type="error">
                <TextContent>
                  <h3>Manual Analysis Step Skipped</h3>
                  <p>
                    To see a proper comparison between manual and AI analysis, please complete the manual analysis step first.
                  </p>
                  <Button onClick={handleGoToManualAnalysis}>Go to Manual Analysis</Button>
                </TextContent>
              </Alert>
            ) : (
              <Alert type="success">
                <TextContent>
                  <h3>AI Analysis: Faster and More Accurate!</h3>
                  <p>
                    The AI completed the document analysis in just {formatTime(aiAnalysisTime)}, compared to 
                    your manual analysis time of {formatTime(manualAnalysisTime)}. This represents a 
                    {timePercentageDifference}% improvement in efficiency.
                  </p>
                  <p>
                    Additionally, the AI achieved {aiAccuracy}% accuracy in answering the questions, compared to 
                    {manualAccuracy.toFixed(0)}% accuracy for the manual analysis.
                  </p>
                </TextContent>
              </Alert>
            )}

            {!manualAnalysisSkipped && (
              <>
                <Header variant="h3">Performance Comparison</Header>
                <BarChart
                  series={[
                    {
                      title: "Manual Analysis",
                      type: "bar",
                      data: [
                        { x: "Time (seconds)", y: manualAnalysisTime },
                        { x: "Accuracy (%)", y: manualAccuracy }
                      ]
                    },
                    {
                      title: "AI Analysis",
                      type: "bar",
                      data: [
                        { x: "Time (seconds)", y: aiAnalysisTime },
                        { x: "Accuracy (%)", y: aiAccuracy }
                      ]
                    }
                  ]}
                  xDomain={["Time (seconds)", "Accuracy (%)"]}
                  yDomain={[0, Math.max(manualAnalysisTime, 100)]}
                  i18nStrings={{
                    xTickFormatter: e => e,
                    yTickFormatter: e => e.toFixed(1)
                  }}
                  ariaLabel="Manual vs AI Analysis Performance"
                  height={300}
                  hideFilter
                  hideLegend={false}
                  horizontalBars
                />
                
                <Box textAlign="center" padding="m">
                  <Button 
                    iconName="gen-ai" 
                    onClick={() => setShowPromptResponseModal(true)}
                  >
                    View Amazon Bedrock Prompt & Response
                  </Button>
                </Box>
              </>
            )}
            
            <Header variant="h3">Question Responses</Header>
            <Table
              columnDefinitions={[
                {
                  id: 'question',
                  header: 'Question',
                  cell: item => item.question
                },
                {
                  id: 'manual',
                  header: 'Your Answer',
                  cell: item => {
                    if (manualAnalysisSkipped) return 'Not completed';
                    
                    const answer = manualAnswers[item.id];
                    if (!answer) return 'Not answered';
                    
                    // Map option values to labels for display
                    if (item.id === 'q1') {
                      return answer;
                    } else if (item.id === 'q2') {
                      const options = {
                        "option1": "Exclusionary screening of controversial industries",
                        "option2": "Integration of ESG factors into investment decisions",
                        "option3": "Active engagement with companies on ESG issues",
                        "option4": "No specific ESG strategy mentioned"
                      };
                      return options[answer] || answer;
                    } else if (item.id === 'q3') {
                      const options = {
                        "yes": "Yes, with detailed explanations",
                        "partial": "Yes, but with limited details",
                        "no": "No section on taxation",
                        "unclear": "Unclear or difficult to find"
                      };
                      return options[answer] || answer;
                    } else if (item.id === 'q4') {
                      const options = {
                        "option1": "0.5% p.a.",
                        "option2": "0.75% p.a.",
                        "option3": "1.0% p.a.",
                        "option4": "1.25% p.a."
                      };
                      return options[answer] || answer;
                    }
                    
                    return answer;
                  }
                },
                {
                  id: 'ai',
                  header: 'AI Answer',
                  cell: item => (
                    <Box>
                      <div>{item.aiAnswer}</div>
                      {item.citations && (
                        <Box color="text-status-info" fontSize="body-s" margin={{ top: 'xxs' }}>
                          <Button 
                            variant="link" 
                            iconName="external" 
                            iconAlign="right"
                            onClick={() => {
                              // Toggle the expanded state for this item
                              const newExpandedItems = expandedItems.includes(item.id)
                                ? expandedItems.filter(id => id !== item.id)
                                : [...expandedItems, item.id];
                              setExpandedItems(newExpandedItems);
                            }}
                          >
                            {expandedItems.includes(item.id) ? 'Hide citations' : 'View citations'}
                          </Button>
                        </Box>
                      )}
                      {expandedItems.includes(item.id) && item.citations && (
                        <Box padding="m" marginTop="s">
                          <CitationPanel answer={item} />
                        </Box>
                      )}
                    </Box>
                  )
                },
                {
                  id: 'correct',
                  header: 'Correct Answer',
                  cell: item => item.correctAnswer
                },
                {
                  id: 'match',
                  header: 'Your Answer Correct?',
                  cell: item => {
                    if (manualAnalysisSkipped) {
                      return <StatusIndicator type="stopped">Not completed</StatusIndicator>;
                    }
                    
                    const userAnswer = manualAnswers[item.id];
                    const isCorrect = userAnswer === item.correctAnswer;
                    
                    return (
                      <StatusIndicator type={isCorrect ? 'success' : 'error'}>
                        {isCorrect ? 'Correct' : 'Incorrect'}
                      </StatusIndicator>
                    );
                  }
                }
              ]}
              items={questionsData}
              sortingDisabled
            />
            
            {/* Persona Next Steps Section */}
            {analysisStatus === 'complete' && selectedFile && selectedFile.persona && (
              <Box padding="m" backgroundColor="background.container" borderRadius="default">
                <SpaceBetween size="m">
                  <Header variant="h3">
                    What's Next for {selectedFile.persona.name}?
                  </Header>
                  
                  <ColumnLayout columns={2} variant="text-grid">
                    <div>
                      <SpaceBetween direction="horizontal" size="m" alignItems="center">
                        <img 
                          src={selectedFile.persona.avatar} 
                          alt={`${selectedFile.persona.name}'s avatar`} 
                          style={{ 
                            width: '80px', 
                            height: '80px', 
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '2px solid #eaeded'
                          }} 
                        />
                        <div>
                          <TextContent>
                            <h4 style={{ margin: '0 0 5px 0' }}>{selectedFile.persona.name}</h4>
                            <div><Badge>{selectedFile.persona.jobTitle}</Badge></div>
                          </TextContent>
                        </div>
                      </SpaceBetween>
                    </div>
                    <div>
                      <Box color="text-status-success" fontSize="heading-s" fontWeight="normal" textAlign="right">
                        {!manualAnalysisSkipped && (
                          <div>
                            <span style={{ fontWeight: 'bold' }}>Time saved:</span> {formatTime(timeDifference)}
                          </div>
                        )}
                        <div>
                          <span style={{ fontWeight: 'bold' }}>Accuracy:</span> {aiAccuracy}%
                        </div>
                      </Box>
                    </div>
                  </ColumnLayout>
                  
                  <Box padding={{ top: 's' }}>
                    <TextContent>
                      <h5 style={{ margin: '10px 0 5px 0' }}>Value Delivered</h5>
                      <p>
                        {selectedFile.persona.valueDelivered}
                      </p>
                      
                      <h5 style={{ margin: '15px 0 5px 0' }}>Next Steps</h5>
                      <p>
                        {selectedFile.persona.nextSteps}
                      </p>
                    </TextContent>
                  </Box>
                  
                  <Alert type="info">
                    <SpaceBetween size="xs">
                      <div>
                        <strong>Business Impact:</strong> By implementing AI-powered document analysis, organizations can reduce analysis time by up to 75%,
                        improve accuracy, and allow financial professionals to focus on high-value activities that directly benefit clients.
                      </div>
                    </SpaceBetween>
                  </Alert>
                </SpaceBetween>
              </Box>
            )}
            
            <Box textAlign="center" padding="m">
              <SpaceBetween direction="horizontal" size="xs">
                {manualAnalysisSkipped && (
                  <Button variant="primary" onClick={handleGoToManualAnalysis}>Go to Manual Analysis</Button>
                )}
                <Button iconName="refresh" onClick={handleReset}>Reset Demo</Button>
              </SpaceBetween>
            </Box>
          </SpaceBetween>
        )}
      </SpaceBetween>

      {/* Start Analysis Modal */}
      <Modal
        visible={showStartModal && !manualAnalysisSkipped}
        onDismiss={() => setShowStartModal(false)}
        header="Start AI Analysis"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button iconName="refresh" onClick={handleReset}>Reset Demo</Button>
              <Button variant="link" onClick={() => setShowStartModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" iconName="gen-ai" onClick={startAnalysis} loading={isLoading}>
                Start Analysis
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <TextContent>
            <h3>Ready to see AI in action?</h3>
            <p>
              You've completed the manual analysis of the document in {formatTime(manualAnalysisTime)}.
            </p>
            <p>
              Now, let's see how quickly and accurately AI can analyze the same document and answer the same questions.
            </p>
          </TextContent>
          
          <Box padding="s">
            <TextContent>
              <h4>Questions for AI Analysis</h4>
              <p>
                The AI will analyze the document to answer these questions:
              </p>
            </TextContent>
            {questionsData && questionsData.length > 0 ? (
              <SupportPromptGroup
                items={questionsData.map(q => ({
                  id: q.id,
                  text: q.question
                }))}
                onItemClick={() => {}}
                ariaLabel="AI Analysis Questions"
              />
            ) : (
              <TextContent>
                <ul>
                  <li>Loading questions...</li>
                </ul>
              </TextContent>
            )}
          </Box>
          
          <TextContent>
            <p>
              Click "Start Analysis" to begin the AI-powered document analysis.
            </p>
          </TextContent>
        </SpaceBetween>
      </Modal>

      {/* Prompt & Response Modal */}
      <PromptResponseModal
        visible={showPromptResponseModal}
        onDismiss={() => setShowPromptResponseModal(false)}
        fileId={selectedFile ? selectedFile.id : 'file1'}
      />
    </Container>
  );
}

export default AnalysisPage;
